import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { profileService } from '../services/profile';
import {
  ProfileResponse,
  ProfileUpdateRequest,
  PasswordChangeRequest,
  SubscriptionPackage,
} from '../types';
import Navbar from '../components/Navbar';
import { useToast } from '../contexts/ToastContext';
import { X } from 'lucide-react';

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { loadUser } = useAuthStore();
  const { showToast } = useToast();
  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  const [packages, setPackages] = useState<SubscriptionPackage[]>([]);
  const [loading, setLoading] = useState(true);

  const [profileForm, setProfileForm] = useState<ProfileUpdateRequest>({ email: '', name: '' });
  const [profileEditing, setProfileEditing] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);

  const [passwordForm, setPasswordForm] = useState<PasswordChangeRequest>({ current_password: '', new_password: '' });
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [passwordChanging, setPasswordChanging] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const [showPackages, setShowPackages] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const [showExtraTokens, setShowExtraTokens] = useState(false);

  useEffect(() => {
    loadProfile();
    loadPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const data = await profileService.getProfile();
      setProfile(data);
      setProfileForm({ email: data.user.email || '', name: data.user.name || '' });
    } catch (err: any) {
      console.error('Failed to load profile:', err);
      showToast('Profil bilgileri yüklenemedi', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadPackages = async () => {
    try {
      const data = await profileService.getSubscriptionPackages();
      setPackages(data);
    } catch (err) {
      console.error('Failed to load packages:', err);
    }
  };

  const handleProfileUpdate = async () => {
    setProfileSaving(true);
    try {
      await profileService.updateProfile(profileForm);
      showToast('Profil güncellendi', 'success');
      setProfileEditing(false);
      await loadProfile();
      await loadUser();
    } catch (err: any) {
      showToast(err?.response?.data?.detail || 'Profil güncellenemedi', 'error');
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordForm.new_password !== passwordConfirm) { showToast('Yeni şifreler eşleşmiyor', 'error'); return; }
    if (passwordForm.new_password.length < 6) { showToast('Şifre en az 6 karakter olmalı', 'error'); return; }
    setPasswordChanging(true);
    try {
      await profileService.changePassword(passwordForm);
      showToast('Şifre değiştirildi', 'success');
      setPasswordForm({ current_password: '', new_password: '' });
      setPasswordConfirm('');
      setShowPasswordForm(false);
    } catch (err: any) {
      showToast(err?.response?.data?.detail || 'Şifre değiştirilemedi', 'error');
    } finally {
      setPasswordChanging(false);
    }
  };

  const handleSubscribe = async (packageType: string) => navigate(`/payment?package=${packageType}`);

  const handleCancelSubscription = async () => {
    if (!window.confirm('Aboneliğini iptal etmek istediğinden emin misin? Mevcut kredilerin dönem sonuna kadar kullanılabilir kalır.')) return;
    setCancelling(true);
    try {
      await profileService.cancelSubscription();
      showToast('Abonelik iptal edildi', 'success');
      await loadProfile();
    } catch (err: any) {
      showToast(err?.response?.data?.detail || 'Abonelik iptal edilemedi', 'error');
    } finally {
      setCancelling(false);
    }
  };

  const handlePurchaseExtraTokens = async (amount: number) => {
    const packageTypeMap: { [key: number]: string } = { 1: 'basic_1', 5: 'pro_5', 10: 'enterprise_10' };
    const packageType = packageTypeMap[amount];
    if (packageType) navigate(`/payment?package=${packageType}`);
    else showToast('Geçersiz kredi paketi', 'error');
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('tr-TR', { year: 'numeric', month: 'short', day: 'numeric' }).toUpperCase();
    } catch { return dateStr; }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-5 h-5 border border-accent border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <span className="eyebrow-mute">/ {`${'PROFİL YÜKLENEMEDİ'}`}</span>
        </div>
      </div>
    );
  }

  const totalTokens = profile.tokens.tokens_remaining + profile.tokens.tokens_used;
  const tokenPercent = totalTokens > 0 ? (profile.tokens.tokens_used / totalTokens) * 100 : 0;
  const initials = (profile.user.name || profile.user.username || '?')
    .split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="max-w-5xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-16 md:pb-24">
        {/* Section header */}
        <div className="flex items-baseline justify-between mb-12">
          <span className="eyebrow"><span className="text-accent">/01</span> · PROFİL</span>
          <span className="eyebrow-mute hidden md:inline">@{profile.user.username.toUpperCase()}</span>
        </div>

        {/* Hero */}
        <div className="flex items-center gap-5 mb-16 pb-12 border-b border-hairline">
          <div className="w-16 h-16 border border-hairline flex items-center justify-center shrink-0">
            <span className="font-serif italic text-2xl text-accent leading-none">{initials}</span>
          </div>
          <div>
            <h1 className="display-h display-md leading-none mb-1">
              {profile.user.name || profile.user.username}
            </h1>
            <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-ink-mute">
              @{profile.user.username}
            </span>
          </div>
        </div>

        {/* /02 Tokens */}
        <section className="mb-16">
          <div className="flex items-baseline justify-between mb-6">
            <span className="eyebrow"><span className="text-accent">/02</span> · KULLANIM</span>
            {profile.subscription?.status === 'active' && (
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-success font-bold">
                ✓ AKTİF
              </span>
            )}
          </div>

          <div className="border border-hairline p-8 md:p-10">
            <div className="flex items-end gap-3 mb-6">
              <span className="font-serif italic text-7xl text-accent leading-none tabular-nums">
                {profile.tokens.tokens_remaining}
              </span>
              <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-ink-mute pb-3">
                KREDİ KALDI
              </span>
            </div>

            {/* Progress bar */}
            <div className="w-full h-px bg-hairline mb-2 overflow-hidden">
              <div
                className="h-px bg-accent transition-all duration-500"
                style={{ width: `${Math.min(tokenPercent, 100)}%` }}
              />
            </div>
            <div className="flex items-center justify-between font-mono text-[10px] tracking-wider uppercase text-ink-mute">
              <span>{profile.tokens.tokens_used} {`KULLANILDI`}</span>
              <span>{totalTokens} {`TOPLAM`}</span>
            </div>

            {profile.subscription && (
              <div className="grid grid-cols-2 gap-y-5 gap-x-8 pt-8 mt-8 border-t border-hairline">
                <div>
                  <span className="eyebrow-mute mb-1 block">PLAN</span>
                  <span className="font-serif italic text-xl text-ink">
                    {profile.subscription.package_type?.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase()) || '—'}
                  </span>
                </div>
                <div>
                  <span className="eyebrow-mute mb-1 block">AYLIK KREDİ</span>
                  <span className="font-serif italic text-xl text-ink tabular-nums">
                    {profile.subscription.monthly_tokens || 0}
                  </span>
                </div>
                <div>
                  <span className="eyebrow-mute mb-1 block">SONRAKİ YENİLEME</span>
                  <span className="font-mono text-[12px] tracking-wider uppercase text-ink">
                    {formatDate(profile.subscription.next_billing_date)}
                  </span>
                </div>
                <div>
                  <span className="eyebrow-mute mb-1 block">AYLIK ÜCRET</span>
                  <span className="font-serif italic text-xl text-ink tabular-nums">
                    ${profile.subscription.package_price?.toFixed(2) || '0.00'}
                  </span>
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 pt-8 mt-8 border-t border-hairline">
              <button onClick={() => setShowExtraTokens(true)} className="btn-accent">
                {`KREDİ SATIN AL`}
                <span aria-hidden>→</span>
              </button>
              {profile.subscription?.status === 'active' ? (
                <>
                  <button onClick={() => setShowPackages(true)} className="btn-ghost">
                    {`PLAN DEĞİŞTİR`}
                  </button>
                  <button
                    onClick={handleCancelSubscription}
                    disabled={cancelling}
                    className="font-mono text-[11px] tracking-[0.18em] uppercase text-signal-red hover:opacity-70 disabled:opacity-50 transition-opacity ml-auto"
                  >
                    {cancelling ? 'İPTAL EDİLİYOR…' : 'İPTAL ET'}
                  </button>
                </>
              ) : (
                <button onClick={() => setShowPackages(true)} className="btn-ghost">
                  {profile.subscription ? 'YENİDEN ABONE OL' : 'PLAN SEÇ'}
                </button>
              )}
            </div>
          </div>
        </section>

        {/* /03 Account */}
        <section className="mb-16">
          <div className="flex items-baseline justify-between mb-6">
            <span className="eyebrow"><span className="text-accent">/03</span> · HESAP BİLGİLERİ</span>
            {!profileEditing && (
              <button
                onClick={() => setProfileEditing(true)}
                className="font-mono text-[11px] tracking-[0.18em] uppercase text-ink-dim hover:text-accent transition-colors"
              >
                DÜZENLE
              </button>
            )}
          </div>

          <div className="border border-hairline">
            {!profileEditing ? (
              <dl className="divide-y divide-hairline">
                <div className="flex items-center justify-between px-6 py-5">
                  <dt className="eyebrow-mute">KULLANICI ADI</dt>
                  <dd className="font-prose text-[15px] text-ink">{profile.user.username}</dd>
                </div>
                <div className="flex items-center justify-between px-6 py-5">
                  <dt className="eyebrow-mute">EMAIL</dt>
                  <dd className="font-prose text-[15px] text-ink">{profile.user.email || '—'}</dd>
                </div>
                <div className="flex items-center justify-between px-6 py-5">
                  <dt className="eyebrow-mute">İSİM</dt>
                  <dd className="font-prose text-[15px] text-ink">{profile.user.name || '—'}</dd>
                </div>
              </dl>
            ) : (
              <div className="p-6 md:p-8 space-y-7">
                <div>
                  <label className="field-label">EMAIL</label>
                  <input
                    type="email"
                    value={profileForm.email}
                    onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                    className="input-cinematic"
                  />
                </div>
                <div>
                  <label className="field-label">İSİM</label>
                  <input
                    type="text"
                    value={profileForm.name}
                    onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                    className="input-cinematic"
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={handleProfileUpdate}
                    disabled={profileSaving}
                    className="btn-accent disabled:opacity-50"
                  >
                    {profileSaving ? 'KAYDEDİLİYOR…' : 'KAYDET'}
                  </button>
                  <button
                    onClick={() => {
                      setProfileEditing(false);
                      setProfileForm({ email: profile.user.email || '', name: profile.user.name || '' });
                    }}
                    className="font-mono text-[11px] tracking-[0.18em] uppercase text-ink-dim hover:text-ink transition-colors"
                  >
                    İPTAL
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* /04 Security */}
        <section className="mb-16">
          <div className="flex items-baseline justify-between mb-6">
            <span className="eyebrow"><span className="text-accent">/04</span> · GÜVENLİK</span>
          </div>

          <div className="border border-hairline">
            {!showPasswordForm ? (
              <div className="flex items-center justify-between px-6 py-6">
                <div>
                  <span className="font-prose text-[15px] text-ink block mb-1">{`Şifre`}</span>
                  <span className="font-prose text-[13px] text-ink-dim">
                    Hesabını korumak için şifreni düzenli olarak değiştir.
                  </span>
                </div>
                <button onClick={() => setShowPasswordForm(true)} className="btn-ghost">
                  DEĞİŞTİR
                </button>
              </div>
            ) : (
              <div className="p-6 md:p-8 space-y-7">
                <div>
                  <label className="field-label">MEVCUT ŞİFRE</label>
                  <input
                    type="password"
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                    className="input-cinematic"
                  />
                </div>
                <div>
                  <label className="field-label">YENİ ŞİFRE</label>
                  <input
                    type="password"
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                    className="input-cinematic"
                  />
                </div>
                <div>
                  <label className="field-label">YENİ ŞİFRE (TEKRAR)</label>
                  <input
                    type="password"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    className="input-cinematic"
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={handlePasswordChange}
                    disabled={passwordChanging}
                    className="btn-accent disabled:opacity-50"
                  >
                    {passwordChanging ? 'DEĞİŞTİRİLİYOR…' : 'DEĞİŞTİR'}
                  </button>
                  <button
                    onClick={() => {
                      setShowPasswordForm(false);
                      setPasswordForm({ current_password: '', new_password: '' });
                      setPasswordConfirm('');
                    }}
                    className="font-mono text-[11px] tracking-[0.18em] uppercase text-ink-dim hover:text-ink transition-colors"
                  >
                    İPTAL
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>

      {/* Plan modal */}
      {showPackages && (
        <PackageModal
          title="PLAN SEÇ"
          subtitle="Kullanılmayan krediler bir sonraki aya devreder."
          onClose={() => setShowPackages(false)}
          packages={packages}
          onSelect={(p) => handleSubscribe(p.package_type)}
          showPopular
          renderRight={(pkg) => (
            <>
              <span className="font-serif italic text-3xl text-ink tabular-nums">
                ${pkg.price.toFixed(2)}
              </span>
              <span className="font-mono text-[10px] tracking-wider uppercase text-ink-mute ml-1">/AY</span>
            </>
          )}
        />
      )}

      {/* Extra tokens modal */}
      {showExtraTokens && (
        <PackageModal
          title="KREDİ SATIN AL"
          subtitle="Mevcut bakiyene ek kredi yükle."
          onClose={() => setShowExtraTokens(false)}
          packages={packages}
          onSelect={(p) => handlePurchaseExtraTokens(p.monthly_tokens)}
          showPopular
          renderRight={(pkg) => (
            <span className="font-serif italic text-3xl text-ink tabular-nums">
              ${pkg.price.toFixed(2)}
            </span>
          )}
        />
      )}
    </div>
  );
};

interface PackageModalProps {
  title: string;
  subtitle: string;
  onClose: () => void;
  packages: SubscriptionPackage[];
  onSelect: (pkg: SubscriptionPackage) => void;
  showPopular?: boolean;
  renderRight: (pkg: SubscriptionPackage) => React.ReactNode;
}

const PackageModal: React.FC<PackageModalProps> = ({ title, subtitle, onClose, packages, onSelect, showPopular, renderRight }) => (
  <div className="fixed inset-0 bg-background/85 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-elevated border border-hairline max-w-xl w-full max-h-[85vh] overflow-y-auto">
      <div className="px-6 py-5 border-b border-hairline flex items-start justify-between gap-4">
        <div>
          <span className="eyebrow mb-2 block">/ {title}</span>
          <p className="font-prose text-[14px] text-ink-dim leading-relaxed">{subtitle}</p>
        </div>
        <button
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center text-ink-mute hover:text-ink transition-colors shrink-0"
          aria-label="Kapat"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="divide-y divide-hairline">
        {packages.map((pkg, i) => {
          const pricePerToken = (pkg.price / pkg.monthly_tokens).toFixed(2);
          const isPopular = showPopular && pkg.monthly_tokens === 5;
          return (
            <button
              key={pkg.package_type}
              onClick={() => onSelect(pkg)}
              className={`w-full text-left px-6 py-5 flex items-center justify-between gap-4 transition-colors hover:bg-accent-soft group ${
                isPopular ? 'bg-accent-soft' : ''
              }`}
            >
              <div className="flex items-center gap-4 min-w-0">
                <span className="font-mono text-[10px] tracking-wider text-ink-mute">
                  /{String(i + 1).padStart(2, '0')}
                </span>
                <div className="min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="font-serif italic text-2xl text-ink leading-none">
                      {pkg.monthly_tokens} <span className="font-mono not-italic text-[11px] text-ink-mute tracking-wider align-top">KREDİ</span>
                    </span>
                    {isPopular && (
                      <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-accent font-bold">
                        ★ POPÜLER
                      </span>
                    )}
                  </div>
                  <span className="font-mono text-[11px] tracking-wider uppercase text-ink-mute">
                    ${pricePerToken}/KREDİ
                  </span>
                </div>
              </div>
              <div className="text-right shrink-0">
                {renderRight(pkg)}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  </div>
);

export default ProfilePage;
